import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TrendingUp, Menu, X, LogOut, LayoutDashboard, Circle } from "lucide-react";

const LINKS = [
  { name: "Pricing", href: "#pricing", testId: "nav-link-pricing" },
  { name: "Academy", href: "#academy", testId: "nav-link-academy" },
];

export const Navbar = ({ onAuthClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const initials = (user?.name || "T").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <nav
      className={`sticky top-0 z-50 backdrop-blur-xl border-b border-white/10 transition-colors duration-200 ${
        scrolled ? "bg-[#070A0E]/95" : "bg-[#070A0E]/80"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a href="#top" data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
            <div className="h-9 w-9 rounded-md bg-brand/15 border border-brand/40 grid place-items-center group-hover:border-brand transition-colors duration-150">
              <TrendingUp className="h-5 w-5 text-brand" />
            </div>
            <div className="leading-none">
              <div className="font-heading font-extrabold tracking-tight text-[15px]">NETROY</div>
              <div className="font-data text-[9px] uppercase tracking-[0.25em] text-slate-500">Forex Academy</div>
            </div>
          </a>

          <div className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => (
              <a
                key={l.name}
                href={l.href}
                data-testid={l.testId}
                className="px-3.5 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-white/5 transition-colors duration-150"
              >
                {l.name}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gain/25 bg-gain/5">
              <Circle className="h-2 w-2 fill-gain text-gain pulse-dot" />
              <span className="font-data text-[10px] uppercase tracking-widest text-gain">Market Open</span>
            </div>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-account-menu-button" className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-white/10 hover:border-brand/40 bg-white/5 transition-colors duration-150">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <span className="h-7 w-7 rounded-full bg-brand/20 text-brand grid place-items-center font-data text-xs font-semibold">{initials}</span>
                    )}
                    <span className="text-sm text-slate-200 max-w-[100px] truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#0C1017] border-white/10 text-slate-200 w-56">
                  <div className="px-2 py-2">
                    <p className="text-sm font-medium text-white truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    <span data-testid="user-tier-profile-badge" className="inline-block mt-2 font-data text-[9px] uppercase tracking-widest px-2 py-0.5 rounded bg-brand/15 text-brand border border-brand/30">
                      {user.role === "admin" ? "Admin Tier" : user.plan === "elite" ? "Elite Member" : user.plan === "pro" ? "Pro Member" : "Free Plan"}
                    </span>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate("/dashboard")} className="focus:bg-white/5 cursor-pointer gap-2">
                    <LayoutDashboard className="h-4 w-4" /> Trader Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-logout-button" onClick={logout} className="focus:bg-loss/10 text-loss focus:text-loss cursor-pointer gap-2">
                    <LogOut className="h-4 w-4" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                data-testid="nav-auth-login-button"
                onClick={onAuthClick}
                className="bg-brand hover:bg-brand-hover text-white font-semibold h-9 px-4"
              >
                Sign In
              </Button>
            )}

            <button className="lg:hidden text-slate-300" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10 bg-[#0C1017] px-4 py-3 space-y-1">
          {LINKS.map((l) => (
            <a
              key={l.name}
              href={l.href}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-md"
            >
              {l.name}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
};
